(() => {
  const theme = window.RasvenisTheme || {};
  const config = theme.motion || {};
  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const enabled = config.enabled !== false;
  const replay = config.replay === true;
  const countersEnabled = config.counters !== false;
  const ambientEnabled = config.ambient !== false;

  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  if (!enabled || reduceMotion) {
    html.classList.add('rv-motion-reduced');
    return;
  }

  html.classList.add('rv-motion-enabled');
  if (config.hover !== false) html.classList.add('rv-motion-hover');

  const itemSelectorGroups = [
    {
      selector: [
        '.rv-section-heading > *',
        '.rv-hero__copy > *',
        '.rv-corporate-hero__copy > *',
        '.rv-page-hero__copy > *',
        '.rv-brand-intro__copy > *',
        '.rv-result__copy > *',
        '.rv-product-story__copy > *',
        '.rv-formula-showcase__head > *',
        '.rv-ideal-for__intro > *',
        '.rv-final-cta__copy > *',
        '.rv-spotlight__copy > *',
        '.rv-routine__header > *',
        '.rv-faq__intro > *',
        '.rv-corp-intro > *',
        '.rv-corp-split__copy > *',
        '.rv-contact-form__head > *',
        '.rv-collection-page__head > *',
        '.rv-blog-hero__grid > *',
        '.rv-article-page__hero > *',
        '.rv-buybox > .rv-buybox-block',
        '.rv-header__inner > *',
        '.rv-announcement__inner > *',
        '.rv-footer__top > *',
        '.rv-footer__bottom > *'
      ].join(','),
      variant: 'soft'
    },
    {
      selector: [
        '.rv-benefits__grid > *',
        '.rv-feature-strip__grid > *',
        '.rv-product-story__trust > *',
        '.rv-formula-showcase__grid > *',
        '.rv-ideal-for__list > *',
        '.rv-routine__grid > *',
        '.rv-reviews__track > *',
        '.rv-latest-blog__grid > *',
        '.rv-home-corporate__grid > *',
        '.rv-info-grid > *',
        '.rv-process-grid > *',
        '.rv-story-timeline > *',
        '.rv-experience-principles > *',
        '.rv-contact-reasons > *',
        '.rv-product-grid > *',
        '.rv-collection-grid > *',
        '.rv-corporate-nav__grid > *',
        '.rv-score-bars > div',
        '.rv-gallery__benefit-rail > *',
        '.rv-gallery__thumbs > *',
        '.rv-check-list > *',
        '.rv-vision-roadmap__steps > *',
        '.rv-legal-articles > *',
        '.rv-cart-items > *',
        '.rv-ugc__track > *',
        '.rv-result__points > *',
        '.rv-spotlight__features > *',
        '.rv-faq__items > *',
        '.rv-comparison tbody > tr',
        '.rv-basic-page__inner > *',
        '.rv-article-page__content > *',
        '.rv-contact-page__info > *',
        '.rv-contact-page__hours > *',
        '.rv-cart-summary > *',
        '.rv-search-results > *' 
      ].join(','),
      variant: 'soft'
    },
    {
      selector: [
        '.rv-hero__media',
        '.rv-corporate-hero__visual',
        '.rv-brand-intro__media',
        '.rv-result__media',
        '.rv-product-story__visual',
        '.rv-formula-showcase__product',
        '.rv-final-cta__media',
        '.rv-spotlight__media',
        '.rv-page-hero__visual',
        '.rv-corp-split__media',
        '.rv-featured-article__media',
        '.rv-article-page__image',
        '.rv-about-page__image',
        '.rv-gallery'
      ].join(','),
      variant: 'scale',
      media: true
    },
    {
      selector: [
        '.rv-comparison__wrap',
        '.rv-faq__items',
        '.rv-reviews__summary > *',
        '.rv-ugc__head > *',
        '.rv-ugc__trust-row > *',
        '.rv-contact-page__grid > *',
        '.rv-legal-layout > *',
        '.rv-mission-duo > *',
        '.rv-experience-guide > *',
        '.rv-vision-roadmap > *',
        '.rv-featured-article > *',
        '.rv-brand-intro__values > *'
      ].join(','),
      variant: 'soft'
    }
  ];

  const ambientSelector = [
    '.rv-corporate-hero__halo',
    '.rv-corporate-hero__seal',
    '.rv-final-cta__orb',
    '.rv-product-story__glow',
    '.rv-formula-showcase__orbit',
    '.rv-page-hero__halo'
  ].join(',');

  const counterSelector = [
    '.rv-score > strong',
    '.rv-score > small',
    '.rv-corporate-hero__proof small',
    '.rv-rating-row a strong',
    '[data-motion-count]'
  ].join(',');

  const dynamicNumberSelector = [
    '[data-fomo-viewers]',
    '[data-fomo-cart]',
    '[data-fomo-sold]',
    '[data-fomo-stock]'
  ].join(',');

  const observedItems = new WeakSet();
  const countedItems = new WeakSet();

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const item = entry.target;
          if (entry.isIntersecting) {
            item.classList.add('is-rv-visible');
            if (countersEnabled && item.matches(counterSelector)) animateCounter(item);
            if (!replay) revealObserver.unobserve(item);
          } else if (replay) {
            item.classList.remove('is-rv-visible');
          }
        });
      }, {
        threshold: clamp(Number(config.threshold || 0.12), 0.01, 0.5),
        rootMargin: '0px 0px -7% 0px'
      })
    : {
        observe(item) {
          item.classList.add('is-rv-visible');
          if (countersEnabled && item.matches(counterSelector)) animateCounter(item);
        },
        unobserve() {}
      };

  function markItem(item, variant = 'soft', index = 0, isMedia = false) {
    if (!item || observedItems.has(item) || item.closest('[data-rv-motion="off"]')) return;
    if (item.hidden) {
      // Hidden review cards are observed after they are revealed.
      item.dataset.rvMotionPending = variant;
      return;
    }
    observedItems.add(item);
    item.classList.add('rv-motion-item');
    item.dataset.rvMotion = variant;
    item.style.setProperty('--rv-motion-index', String(index % 10));
    if (isMedia) item.classList.add('rv-motion-media');
    revealObserver.observe(item);
  }

  function parseCounter(element) {
    const raw = element.textContent.trim();
    const match = raw.match(/-?\d+(?:[.,]\d+)?/);
    if (!match) return null;
    const token = match[0];
    const normalized = token.replace(',', '.');
    const target = Number(normalized);
    if (!Number.isFinite(target)) return null;
    const separator = token.includes(',') ? ',' : token.includes('.') ? '.' : '';
    const decimals = separator ? token.split(separator)[1].length : 0;
    return {
      raw,
      token,
      target,
      decimals,
      separator,
      prefix: raw.slice(0, match.index),
      suffix: raw.slice((match.index || 0) + token.length)
    };
  }

  function formatCounter(value, parsed) {
    const fixed = parsed.decimals > 0
      ? Number(value).toFixed(parsed.decimals)
      : String(Math.round(value));
    return parsed.separator === ',' ? fixed.replace('.', ',') : fixed;
  }

  function animateCounter(element) {
    if (countedItems.has(element)) return;
    const parsed = parseCounter(element);
    if (!parsed || Math.abs(parsed.target) > 10000000) return;
    countedItems.add(element);
    const duration = clamp(Number(config.counterDuration || 1050), 350, 2400);
    const startedAt = performance.now();
    const start = parsed.target > 20 ? Math.max(0, parsed.target * .72) : 0;

    const frame = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = start + ((parsed.target - start) * eased);
      element.textContent = `${parsed.prefix}${formatCounter(value, parsed)}${parsed.suffix}`;
      if (progress < 1) requestAnimationFrame(frame);
      else element.textContent = parsed.raw;
    };

    requestAnimationFrame(frame);
  }

  function initScope(context = document) {
    itemSelectorGroups.forEach((group) => {
      qsa(group.selector, context).forEach((item, index) => {
        markItem(item, group.variant, index, Boolean(group.media));
      });
    });

    qsa(counterSelector, context).forEach((item, index) => {
      markItem(item, 'soft', index);
    });

    qsa('.rv-score-bars b', context).forEach((bar, index) => {
      if (observedItems.has(bar)) return;
      observedItems.add(bar);
      bar.classList.add('rv-motion-progress');
      bar.style.setProperty('--rv-motion-index', String(index));
      revealObserver.observe(bar);
    });

    if (ambientEnabled) {
      qsa(ambientSelector, context).forEach((item, index) => {
        item.classList.add('rv-motion-ambient');
        item.style.setProperty('--rv-ambient-duration', `${9 + (index % 4) * 1.5}s`);
        item.style.setProperty('--rv-ambient-delay', `${-1 * (index % 5)}s`);
      });
    }

    qsa(dynamicNumberSelector, context).forEach(watchDynamicNumber);
  }

  function watchDynamicNumber(node) {
    if (node.dataset.rvNumberWatch === 'true') return;
    node.dataset.rvNumberWatch = 'true';
    let locked = false;
    const observer = new MutationObserver(() => {
      if (locked) return;
      locked = true;
      node.classList.remove('rv-number-bump');
      void node.offsetWidth;
      node.classList.add('rv-number-bump');
      window.setTimeout(() => {
        node.classList.remove('rv-number-bump');
        locked = false;
      }, 460);
    });
    observer.observe(node, { childList: true, characterData: true, subtree: true });
  }

  function revealLateReviews(context = document) {
    qsa('[data-imported-review][data-rv-motion-pending]:not([hidden])', context).forEach((card, index) => {
      delete card.dataset.rvMotionPending;
      card.classList.add('rv-motion-late');
      card.style.setProperty('--rv-motion-index', String(index % 10));
      window.setTimeout(() => card.classList.remove('rv-motion-late'), 1200);
      markItem(card, 'soft', index);
    });
  }

  const documentObserver = new MutationObserver((mutations) => {
    let shouldScan = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) shouldScan = true;
      });
      if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') shouldScan = true;
    });
    if (!shouldScan) return;
    window.requestAnimationFrame(() => {
      revealLateReviews();
      initScope();
    });
  });

  function start() {
    const duration = clamp(Number(config.duration || 720), 300, 1600);
    html.style.setProperty('--rv-motion-duration', `${duration}ms`);
    html.style.setProperty('--rv-motion-duration-fast', `${Math.round(duration * .72)}ms`);
    html.style.setProperty('--rv-motion-duration-slow', `${Math.round(duration * 1.38)}ms`);
    html.style.setProperty('--rv-motion-distance', `${clamp(Number(config.distance || 18), 4, 48)}px`);
    html.style.setProperty('--rv-motion-stagger', `${clamp(Number(config.stagger || 70), 0, 220)}ms`);
    initScope();
    documentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    qsa('.rv-motion-item', event.target).forEach((item) => item.classList.add('is-rv-visible'));
    event.target.classList.add('is-rv-visible');
  });
})();
